/* Header language switcher (snippets/rs-header-lang.liquid).
   Opens and closes the language list, moves focus with the arrow keys, closes on Esc, a click outside or
   when focus leaves, and posts Shopify's localization form with the picked language. */

class HeaderLang extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.header__icon--lang');
    this.list = this.querySelector('.header__lang-list');
    this.input = this.querySelector('input[name="locale_code"]');
    this.form = this.querySelector('form');
    this.links = [...this.querySelectorAll('.header__lang-link')];
    if (!this.button || !this.list || !this.input || !this.form) return;

    this.controller = new AbortController();
    const { signal } = this.controller;

    this.button.addEventListener('click', () => this.toggle(), { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.addEventListener('focusout', (event) => this.onFocusout(event), { signal });
    document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });

    for (const link of this.links) {
      link.addEventListener('click', (event) => this.onLinkClick(event, link), { signal });
    }
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
  }

  get isOpen() {
    return this.button.getAttribute('aria-expanded') === 'true';
  }

  open() {
    this.button.setAttribute('aria-expanded', 'true');
    this.list.removeAttribute('hidden');
  }

  close() {
    this.button.setAttribute('aria-expanded', 'false');
    this.list.setAttribute('hidden', '');
  }

  toggle() {
    if (this.isOpen) {
      this.close();
      return;
    }
    this.open();
  }

  focusLink(index) {
    const count = this.links.length;
    const link = this.links[(index + count) % count];
    if (link) link.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      this.button.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    if (!this.isOpen) this.open();

    const down = event.key === 'ArrowDown';
    const current = this.links.indexOf(document.activeElement);
    if (current === -1) {
      const active = this.links.findIndex((link) => link.hasAttribute('aria-current'));
      this.focusLink(down ? Math.max(active, 0) : this.links.length - 1);
      return;
    }
    this.focusLink(current + (down ? 1 : -1));
  }

  onFocusout(event) {
    if (!this.isOpen) return;
    if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onLinkClick(event, link) {
    event.preventDefault();
    const code = link.dataset.value;
    this.close();
    if (!code || code === this.input.value) return;
    this.input.value = code;
    this.form.submit();
  }
}

if (!customElements.get('header-lang')) {
  customElements.define('header-lang', HeaderLang);
}
